import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import './square-image.scss';
import { useCachedImage } from "./useCachedImage";
import { ArtistDTO, Song } from "../../view/spotipitan/types";

interface Props {
    src?: string;
    alt?: string;
    className?: string;
    style?: React.CSSProperties;
    overDetails?: React.ReactNode;
    hideOverImg?: boolean;
    loading?: boolean;
    hideBackground?: boolean; // Se conserva para mantener compatibilidad de firmas de props
    fill?: boolean; // Si se debe llenar el contenedor
    song?: Song;
    playlist?: ArtistDTO;
    big?: boolean;
}

const SquareImage = ({ src, alt, className, style, overDetails, hideOverImg, loading, fill, song, playlist, big }: Props) => {

    const containerRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isRenderedWide, setIsRenderedWide] = useState(false);

    const { imageUrl } = useCachedImage({ song, playlist, big, src, isVisible });
    const [currentSrc, setCurrentSrc] = useState<string | undefined>(imageUrl);

    useEffect(() => {
        setCurrentSrc(imageUrl);
    }, [imageUrl]);

    const handleImageError = () => {
        if (song?.poster_url && currentSrc !== song.poster_url) {
            setCurrentSrc(song.poster_url);
            return;
        }
        if (src && currentSrc !== src) {
            setCurrentSrc(src);
            return;
        }
        setIsLoaded(true);
    };

    useEffect(() => {
        setIsLoaded(false);

        let timer: NodeJS.Timeout;
        const checkComplete = () => {
            if (imgRef.current?.complete) {
                setIsLoaded(true);
                return true;
            }
            return false;
        };

        if (imageUrl && isVisible) {
            // Verificar inmediatamente por si el navegador la cargó al instante
            if (!checkComplete()) {
                // Polling de respaldo para capturar el estado completado
                timer = setInterval(() => {
                    if (checkComplete()) {
                        clearInterval(timer);
                    }
                }, 50);
            }
        }

        return () => {
            if (timer) clearInterval(timer);
        };
    }, [imageUrl, isVisible]);

    // Detect visibility of the component and observe resizes
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            {
                root: null, // viewport
                threshold: 0.1 // fire when 10% visible
            }
        );
        observer.observe(el);

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect;
                if (width && height) {
                    setIsRenderedWide(width / height > 16 / 9);
                }
            }
        });
        resizeObserver.observe(el);

        return () => {
            observer.disconnect();
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className={classNames("square-image", className)}
            style={style}
        >
            {isVisible && (currentSrc || imageUrl) && (
                <img
                    ref={imgRef}
                    hidden={hideOverImg}
                    style={{ ...style, objectFit: (isRenderedWide) ? "contain" : undefined }}
                    className={classNames("square-image__front-image", "fill", { "loaded": isLoaded })}
                    alt={alt}
                    src={currentSrc || imageUrl}
                    onError={handleImageError}
                    onLoad={(e) => {
                        setIsLoaded(true);
                    }}
                />
            )}
            <div className="square-image--over-details">{overDetails}</div>
        </div>
    );
}

export default SquareImage;
